/**
 * Vorlage „In Streifen schneiden“.
 *
 * Draufsicht: Eine flache Zutat (Paprikahälfte oder Fleisch) wird quer in
 * gleich breite Streifen geschnitten. Die Streifen lösen sich und liegen
 * zum Schluss locker nebeneinander.
 */
import { ease, lerp, random, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { blobPoints, path, smooth } from '../paths.ts';
import { compact, faded, group, line, rect, rotated, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';
import { crossCutDone, crossCutPose, knifeSquash, piece } from '../parts/cutting.ts';
import { board, knife } from '../parts/knife.ts';
import type { Pose } from '../parts/pose.ts';
import type { Produce } from '../parts/produce.ts';
import { arrow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Lage der Zutat. */
const BOX = { x: 36, y: 36, width: 104, height: 56 } as const;
const RIGHT = BOX.x + BOX.width;
const KNIFE_SCALE = 0.82;
const PARK: Pose = { x: 176, y: 128, rotate: -4, lift: 0.6 };

/** Zeitplan. */
const TIME = {
  appear: [0, 0.05],
  approach: [0.04, 0.1],
  cut: [0.1, 0.72],
  park: [0.72, 0.8],
  spread: [0.74, 0.86],
  vanish: [0.93, 1],
} as const;

/** Streifenbreite je Stufe. */
const WIDTHS = { thin: 8, medium: 12, wide: 17 } as const;
type Width = keyof typeof WIDTHS;

/** Flache Zutaten. */
const ITEMS = { pepper: 'Paprika', meat: 'Fleisch' } as const;
type Item = keyof typeof ITEMS;

/** Paprikahälfte oder Fleischstück als schneidbare Zutat. */
function flatItem(item: Item): Produce {
  const cx = BOX.x + BOX.width / 2;
  const cy = BOX.y + BOX.height / 2;
  if (item === 'pepper') {
    const outline = smooth(
      blobPoints(cx, cy, BOX.width / 2, BOX.height / 2, 16, (angle) => 1 + 0.05 * Math.cos(angle * 4)),
      true,
    );
    return {
      outline,
      fill: 'deep',
      details: [
        shape(
          path()
            .move(cx - BOX.width * 0.32, cy - BOX.height * 0.22)
            .quad(cx - BOX.width * 0.05, cy - BOX.height * 0.42, cx + BOX.width * 0.3, cy - BOX.height * 0.24)
            .toString(),
          { stroke: 'paper', strokeWidth: STROKE.outline },
        ),
      ],
      extras: [rect(BOX.x - 9, cy - 5, 12, 10, 3, styles.solid('shade'))],
      keepRight: 0,
      pieceFill: 'deep',
    };
  }
  const rng = random(9);
  const bumps = Array.from({ length: 12 }, () => 0.95 + rng() * 0.07);
  const outline = smooth(
    blobPoints(cx, cy, BOX.width / 2, BOX.height / 2, 12, (_angle, index) => bumps[index] ?? 1),
    true,
  );
  const fat: SceneNode[] = [-0.25, 0.05, 0.3].map((f, index) =>
    shape(
      path()
        .move(BOX.x + 10, cy + BOX.height * f)
        .curve(
          cx - 20,
          cy + BOX.height * f - 8 + index * 4,
          cx + 10,
          cy + BOX.height * f + 6,
          RIGHT - 8,
          cy + BOX.height * f - 2,
        )
        .toString(),
      { stroke: 'paper', strokeWidth: STROKE.detail },
    ),
  );
  return { outline, fill: 'shade', details: fat, extras: [], keepRight: 0, pieceFill: 'shade' };
}

/** Vorlage „In Streifen schneiden“. */
export const cutStripsTemplate = defineTemplate({
  id: 'cut.strips',
  group: 'cut',
  title: 'In Streifen schneiden',
  description: 'Die Zutat liegt flach auf dem Brett und wird quer in gleich breite Streifen geschnitten.',
  durationSeconds: 7,
  keywords: ['streifen', 'in streifen', 'stifte', 'julienne', 'geschnetzeltes', 'schnetzeln'],
  stillTime: 0.9,
  parameters: [
    choice('item', 'Zutat', ITEMS, 'pepper'),
    choice('width', 'Breite', { thin: 'schmal', medium: 'mittel', wide: 'breit' }, 'medium'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      item: /paprika|schote/.test(lower)
        ? 'pepper'
        : /fleisch|hähnchen|brust|filet|schnetzel/.test(lower)
          ? 'meat'
          : undefined,
      width: /fein|schmal|dünn|julienne/.test(lower) ? 'thin' : /breit|grob/.test(lower) ? 'wide' : undefined,
    };
  },
  scene(values, t) {
    const item = flatItem(textValue(values, 'item', 'pepper') as Item);
    const width = WIDTHS[textValue(values, 'width', 'medium') as Width];
    const count = Math.floor(BOX.width / width) - 1;
    const cutXs = Array.from({ length: count }, (_, index) => RIGHT - (index + 1) * width);
    const visible = Math.min(
      ease.outCubic(segment(t, ...TIME.appear)),
      1 - ease.inCubic(segment(t, ...TIME.vanish)),
    );
    const spread = ease.outCubic(segment(t, ...TIME.spread));

    const strips: MaybeNode[] = [];
    let done = 0;
    for (let index = 0; index <= count; index += 1) {
      // Der letzte Streifen ist der Rest links, er löst sich mit dem letzten Schnitt.
      const at = crossCutDone(Math.min(index, count - 1), count, TIME.cut);
      if (t < at) {
        break;
      }
      done = index + 1;
      const loosen = ease.outBack(segment(t, at, at + 0.05));
      const x0 = index === count ? BOX.x - 12 : RIGHT - (index + 1) * width;
      const x1 = index === 0 ? RIGHT + 12 : RIGHT - index * width;
      const rng = random(index * 13 + 5);
      const dx = 5 * loosen + (count - index) * 2.4 * spread + (rng() - 0.5) * 4 * spread;
      const dy = (rng() - 0.5) * 8 * spread;
      const cell = { x0, x1, y0: BOX.y - 12, y1: BOX.y + BOX.height + 12 };
      const cx = (Math.max(x0, BOX.x) + Math.min(x1, RIGHT)) / 2 + dx;
      strips.push(
        rotated((rng() - 0.5) * 16 * spread, cx, BOX.y + BOX.height / 2 + dy, [
          piece(item, cell, dx, dy, true),
        ]),
      );
    }
    const restRight = done === 0 ? RIGHT + 12 : RIGHT - done * width;
    const rest =
      done <= count
        ? group([
            piece(
              item,
              { x0: BOX.x - 12, x1: restRight, y0: BOX.y - 12, y1: BOX.y + BOX.height + 12 },
              0,
              0,
              false,
            ),
            done > 0
              ? group([line(restRight, BOX.y - 4, restRight, BOX.y + BOX.height + 4, styles.detail())], {
                  clip: item.outline,
                })
              : null,
          ])
        : null;
    return compact([
      ...board(),
      faded(visible, [rest, ...strips]),
      knife({
        ...crossCutPose(t, cutXs, BOX.y + BOX.height + 4, PARK, TIME),
        scale: KNIFE_SCALE,
        squash: knifeSquash(t, TIME),
      }),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: RIGHT - 4, y: BOX.y - 12 },
        { x: lerp(BOX.x, RIGHT, 0.3), y: BOX.y - 12 },
      ]),
    ];
  },
});
